import React, { useMemo } from "react";
import "./styles.scss";
import { Box } from "@material-ui/core";
import { IPassengerCounts, IExperiencesPassengerCounts } from "redmond";
import clsx from "clsx";
import { max, min } from "lodash-es";
import { ActionButton, ActionLink, Icon, IconName } from "halifax";

export const MINIMUM_ADULTS_COUNT = 1;
export const MAXIMUM_TRAVELERS_COUNT = 6;

export interface PassengerCountLimits {
  seniorsCount?: { min?: number; max?: number };
  adultsCount: { min?: number; max?: number };
  youthsCount?: { min?: number; max?: number };
  childrenCount: { min?: number; max?: number };
  infantsCount?: { min?: number; max?: number };
}

export type PassengerCountPickerType = IExperiencesPassengerCounts;

const getSeniorsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IPassengerCounts
  if ("seniorsCount" in counts) {
    return counts.seniorsCount || 0;
  }
  return 0;
};

const getAdultsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IPassengerCounts
  if ("adultsCount" in counts) {
    return counts.adultsCount;
  }
  return 0;
};

const getYouthsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IPassengerCounts
  if ("youthsCount" in counts) {
    return counts.youthsCount || 0;
  }
  return 0;
};

const getChildrenCount = (counts: PassengerCountPickerType) => {
  if ("childrenCount" in counts) {
    return counts.childrenCount || 0;
  }

  return 0;
};

const getInfantsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IPassengerCounts
  if ("infantsCount" in counts) {
    return counts.infantsCount || 0;
  }
  return 0;
};

export interface IPassengerCountPickerProps {
  onClickApply?: (counts: PassengerCountPickerType) => void;
  setPassengerCounts: (passengerCounts: PassengerCountPickerType) => void;
  counts: PassengerCountPickerType;
  minimumAdultsCount?: number;
  maximumChildrenCount?: number;
  maximumRoomsCount?: number;
  minimumTravelersCount?: number;
  maximumTravelersCount?: number;
  titles?: PassengerCountPickerTitles;
  warningMessage?: string;
  unlimited?: boolean;
  className?: string;
  buttonText?: string;
  enableButtonOnChangeOnly?: boolean;
  showChildAgeInputs?: boolean;
  showInfantAgeInputs?: boolean;
  getPaxCountsError?: (counts: PassengerCountPickerType) => string | null; // Returns an error string if pax counts are invalid
  paxCountLimits?: PassengerCountLimits;
}

export type PassengerCountPickerTitles = {
  modalTitle: string;
  seniorTitle?: string;
  seniorPrice?: (count: number) => string;
  seniorSubtitle?: string;
  seniorHelperText?: string;
  adultTitle: string;
  adultPrice?: (count: number) => string;
  adultSubtitle: string;
  adultHelperText?: string;
  youthTitle?: string;
  youthPrice?: (count: number) => string;
  youthSubtitle?: string;
  youthHelperText?: string;
  childrenTitle: string;
  childrenPrice?: (count: number) => string;
  childrenSubtitle?: string;
  childrenHelperText?: string;
  childAgeInputSubtitle?: string;
  infantSeatTitle?: string;
  infantSeatSubtitle?: string;
  infantLapTitle?: string;
  infantLapSubtitle?: string;
  infantTitle?: string;
  infantSubtitle?: string;
  infantHelperText?: string;
  infantPrice?: (count: number) => string;
};

export const PassengerCountPicker = (props: IPassengerCountPickerProps) => {
  const {
    onClickApply,
    setPassengerCounts,
    getPaxCountsError,
    counts,
    titles = {
      modalTitle: "Edit Travelers",
      adultTitle: "Adults",
      adultSubtitle: "",
      childrenTitle: "Children",
      childrenSubtitle: "Ages 2-11",
      childAgeInputSubtitle:
        "To ensure the most accurate rates, please enter the exact age of the children on this booking.",
      infantLapTitle: "Infants on lap",
      infantLapSubtitle: "Under the age of 2",
      infantSeatTitle: "Infants in seat",
      infantSeatSubtitle: "Under the age of 2",
      infantTitle: "Infants",
      infantSubtitle: "Younger than 2",
    },
    minimumAdultsCount = MINIMUM_ADULTS_COUNT,
    minimumTravelersCount = MINIMUM_ADULTS_COUNT,
    maximumTravelersCount = MAXIMUM_TRAVELERS_COUNT,
    warningMessage = "",
    unlimited,
    className,
    buttonText,
    enableButtonOnChangeOnly = false,
    paxCountLimits,
  } = props;
  const [seniorsCount, setSeniorsCount] = React.useState<number>(
    getSeniorsCount(counts)
  );
  const [adultsCount, setAdultsCount] = React.useState<number>(
    getAdultsCount(counts)
  );
  const [youthsCount, setYouthsCount] = React.useState<number>(
    getYouthsCount(counts)
  );

  const [infantsCount, setInfantsCount] = React.useState<number>(
    getInfantsCount(counts)
  );
  const [childrenCount, setChildrenCount] = React.useState<number>(
    getChildrenCount(counts)
  );

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  // const handleSetChildrenCount = (
  //   getNewChildrenCount: (prevCount: number) => number
  // ) => {
  //   const countDifference =
  //     getNewChildrenCount(children.length) - children.length;

  //   if (countDifference > 0) {
  //     setChildren((prevChildren) =>
  //       prevChildren.concat(Array(countDifference).fill(DEFAULT_CHILD_AGE))
  //     );
  //   } else if (countDifference < 0) {
  //     setChildren((prevChildren) => {
  //       const newChildren = [...prevChildren];
  //       newChildren.splice(
  //         children.length - Math.abs(countDifference),
  //         Math.abs(countDifference)
  //       );

  //       return newChildren;
  //     });
  //   }
  // };

  // const handleSetInfantsCount = (
  //   getNewInfantsCount: (prevCount: number) => number
  // ) => {
  //   const countDifference = getNewInfantsCount(infants.length) - infants.length;

  //   if (countDifference > 0) {
  //     setInfants((prevInfants) =>
  //       prevInfants.concat(
  //         Array(countDifference).fill({ age: 1, inSeat: false })
  //       )
  //     );
  //   } else if (countDifference < 0) {
  //     setInfants((prevInfants) => {
  //       const newInfants = [...prevInfants];
  //       newInfants.splice(
  //         infants.length - Math.abs(countDifference),
  //         Math.abs(countDifference)
  //       );

  //       return newInfants;
  //     });
  //   }
  // };

  const isTooManyPassengers =
    !unlimited &&
    adultsCount + childrenCount + seniorsCount + youthsCount + infantsCount >
      maximumTravelersCount;

  const isTooLittleTravelers =
    minimumTravelersCount !== 0 &&
    adultsCount + childrenCount + seniorsCount + youthsCount + infantsCount <
      minimumTravelersCount;

  const applyDisabled = isTooManyPassengers || isTooLittleTravelers;

  const remainingLimitedTravelersCount =
    max([
      maximumTravelersCount -
        (childrenCount +
          adultsCount +
          youthsCount +
          seniorsCount +
          infantsCount),
      0,
    ]) || 0;

  const handleSetPassengerCounts = () => {
    if ("adultsCount" in counts) {
      setPassengerCounts({
        seniorsCount,
        adultsCount,
        youthsCount,
        childrenCount,
        infantsCount,
      });
    }
  };

  const paxCountsUpdated = useMemo(() => {
    const paxCount = counts as IPassengerCounts;
    return (
      "adultsCount" in counts &&
      (paxCount.adultsCount !== adultsCount ||
        paxCount.seniorsCount !== seniorsCount ||
        paxCount.youthsCount !== youthsCount ||
        paxCount.infantsCount !== infantsCount ||
        paxCount.childrenCount !== childrenCount)
    );
  }, [adultsCount, seniorsCount, youthsCount, infantsCount, childrenCount]);

  return (
    <Box className={clsx("passenger-count-picker-root", className)}>
      <Box className="passenger-count-picker-container">
        <Box
          id="passenger-count-picker"
          className="passenger-count-picker-title"
        >
          {titles.modalTitle}
        </Box>
        <Box className={clsx("passenger-count-picker-types-section")}>
          {titles.seniorTitle &&
            renderCountPick({
              title: titles.seniorTitle,
              subtitle: titles.seniorSubtitle,
              helperText: titles.seniorHelperText,
              price: titles.seniorPrice?.(seniorsCount),
              className: titles.seniorSubtitle || titles.seniorTitle,
              minimumCount: paxCountLimits?.seniorsCount?.min || 0,
              maximumCount: (() => {
                switch (true) {
                  case unlimited:
                    return Infinity;
                  default:
                    return min([
                      remainingLimitedTravelersCount + seniorsCount,
                      paxCountLimits?.seniorsCount?.max,
                    ]);
                }
              })(),
              count: seniorsCount,
              setCount: setSeniorsCount,
            })}
          {renderCountPick({
            title: titles.adultTitle,
            subtitle: titles.adultSubtitle,
            helperText: titles.adultHelperText,
            price: titles.adultPrice?.(adultsCount),
            className: titles.adultSubtitle,
            minimumCount:
              minimumAdultsCount || paxCountLimits?.adultsCount.min || 0,
            maximumCount: (() => {
              switch (true) {
                case unlimited:
                  return Infinity;
                default:
                  return min([
                    remainingLimitedTravelersCount + adultsCount,
                    paxCountLimits?.adultsCount?.max,
                  ]);
              }
            })(),
            count: adultsCount,
            setCount: setAdultsCount,
          })}
          {titles.youthTitle &&
            renderCountPick({
              title: titles.youthTitle,
              subtitle: titles.youthSubtitle,
              helperText: titles.youthHelperText,
              price: titles.youthPrice?.(youthsCount),
              className: titles.youthSubtitle || titles.youthTitle,
              minimumCount: paxCountLimits?.youthsCount?.min || 0,
              maximumCount: (() => {
                switch (true) {
                  case unlimited:
                    return Infinity;
                  default:
                    return min([
                      remainingLimitedTravelersCount + youthsCount,
                      paxCountLimits?.youthsCount?.max,
                    ]);
                }
              })(),
              count: youthsCount,
              setCount: setYouthsCount,
            })}
          {titles.childrenTitle &&
            renderCountPick({
              title: titles.childrenTitle,
              price: titles.childrenPrice?.(childrenCount),
              subtitle: titles.childrenSubtitle,
              helperText: titles.childrenHelperText,
              className: "children",
              count: childrenCount,
              setCount: setChildrenCount,
              maximumCount: min([
                remainingLimitedTravelersCount + childrenCount,
                paxCountLimits?.childrenCount?.max,
              ]),
            })}
          {titles.infantTitle &&
            renderCountPick({
              title: titles.infantTitle,
              subtitle: titles.infantSubtitle,
              helperText: titles.infantHelperText,
              price: titles.infantPrice?.(infantsCount),
              className: titles.infantSubtitle || titles.infantTitle,
              minimumCount: 0,
              maximumCount: min([
                remainingLimitedTravelersCount + infantsCount,
                paxCountLimits?.infantsCount?.max,
              ]),
              count: infantsCount,
              setCount: setInfantsCount,
            })}
        </Box>
        {errorMessage && renderErrorMessage(errorMessage)}
        {renderWarningMessage({
          maximumTravelersCount,
          warningMessage,
          unlimited,
        })}
        <ActionButton
          className={clsx("passenger-count-change-button-wrapper", className)}
          buttonClassName="passenger-count-change-button"
          disabled={
            (!enableButtonOnChangeOnly && applyDisabled) ||
            (enableButtonOnChangeOnly && !paxCountsUpdated)
          }
          message={buttonText || "Apply"}
          onClick={() => {
            const errorMessage = getPaxCountsError
              ? getPaxCountsError({
                  seniorsCount,
                  adultsCount,
                  youthsCount,
                  childrenCount: childrenCount,
                  infantsCount,
                })
              : null;
            setErrorMessage(errorMessage);
            if (!errorMessage) {
              handleSetPassengerCounts();
              onClickApply?.({
                seniorsCount,
                adultsCount,
                youthsCount,
                childrenCount,
                infantsCount,
              });
            }
          }}
        />
      </Box>
    </Box>
  );
};

const renderErrorMessage = (message: string) => {
  if (!message) return;

  return <Box className="passenger-count-picker-error-message">{message}</Box>;
};

const renderWarningMessage = (props: {
  maximumTravelersCount?: number;
  warningMessage?: string;
  unlimited?: boolean;
}) => {
  const { maximumTravelersCount, warningMessage, unlimited } = props;

  const isVisible = !unlimited;

  if (!isVisible) return;

  const getDisplayMessage = () => {
    if (warningMessage) return warningMessage;
    if (!unlimited)
      return `You can search for up to ${maximumTravelersCount} travelers`;
    return "";
  };

  return (
    <Box className="passenger-count-picker-warning-message">
      {getDisplayMessage()}
    </Box>
  );
};

const renderCountPick = (props: {
  title: string;
  subtitle?: string | JSX.Element;
  helperText?: string | JSX.Element;
  price?: string;
  className: string;
  minimumCount?: number;
  maximumCount?: number;
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const {
    title,
    subtitle,
    helperText,
    price,
    className,
    count,
    setCount,
    minimumCount,
    maximumCount,
  } = props;

  const isMinusDisabled = count <= (minimumCount ?? 0);
  const isPlusDisabled = count >= (maximumCount ?? Infinity);

  return (
    <Box className={clsx("passenger-count-picker-type", className)}>
      <Box className="passenger-type-description-container">
        <Box className="passenger-type-description">
          <Box className="passenger-type-title">{title}</Box>
          {price && <Box className="passenger-type-price">{price}</Box>}
          {subtitle && (
            <Box className="passenger-type-subtitle">{subtitle}</Box>
          )}
          {helperText && (
            <Box className="passenger-type-helper-text">{helperText}</Box>
          )}
        </Box>
      </Box>
      <Box className="passenger-count-picker-container">
        <ActionLink
          label={`Decrease ${title} ${subtitle || ""} Traveler`}
          className={clsx("passenger-count-picker-button", "minus")}
          onClick={() => {
            if (!isMinusDisabled) {
              setCount((prevCount) => prevCount - 1);
            }
          }}
          content={
            <Box
              className={clsx("button-content-wrapper", {
                disabled: isMinusDisabled,
              })}
            >
              <Icon
                name={IconName.MinusIcon}
                className={clsx("button-content minus-icon", {
                  disabled: isMinusDisabled,
                })}
              />
            </Box>
          }
        />
        <Box className="passenger-count-number-container">
          <Box className="passenger-count-number">
            {count}
            <span
              className="passenger-count-alert"
              aria-live="polite"
              aria-atomic={true}
            >
              {subtitle ? `${count} ${title} ${subtitle}` : `${count} ${title}`}
            </span>
          </Box>
        </Box>

        <ActionLink
          label={`Increase ${title} ${subtitle || ""} Traveler`}
          className={clsx("passenger-count-picker-button", "plus")}
          onClick={() => {
            if (!isPlusDisabled) {
              setCount((prevCount) => prevCount + 1);
            }
          }}
          content={
            <Box
              className={clsx("button-content-wrapper", {
                disabled: isPlusDisabled,
              })}
            >
              <Icon
                name={IconName.PlusIcon}
                className={clsx("button-content plus-icon", {
                  disabled: isPlusDisabled,
                })}
              />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
